import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPlay, FaPause } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const AUDIO_URL = 'https://archive.org/download/KennyGSongBird/KennyGSaxSongBird_64kb.mp3';
const API_URL = 'https://api.hidrogen.id/api/hits';
const INITIAL_VOLUME = 0.5;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FooterWrap = styled.footer`
  background: #03045e;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
`;

// ── BIG STATEMENT ROW ──────────────────────────────────────
const StatementRow = styled.div`
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 4rem;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 3rem 2rem;
    text-align: center;
  }
`;

const BigText = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -3px;
  line-height: 1;

  em {
    font-style: normal;
    color: #00b4d8;
  }
`;

const VisitorBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  gap: 0.3rem;

  @media (max-width: 900px) { align-items: center; }

  label {
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #00b4d8;
  }

  strong {
    font-size: clamp(2rem, 3vw, 2.8rem);
    font-weight: 900;
    color: #ffffff;
    letter-spacing: -2px;
    line-height: 1;
  }
`;

// ── BOTTOM ROW ─────────────────────────────────────────────
const BottomRow = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.5rem 2rem;
    text-align: center;
    gap: 1.2rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) { justify-content: center; }

  a {
    color: rgba(255,255,255,0.3);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    transition: color 0.25s ease;

    &:hover { color: #00b4d8; }

    &.lowercase { text-transform: none; }
  }
`;

const Copyright = styled.p`
  color: rgba(255,255,255,0.2);
  font-size: 0.72rem;
  letter-spacing: 1px;
  white-space: nowrap;
`;

// ── AUDIO CONTROL ──────────────────────────────────────────
const AudioControl = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: rgba(3,4,94,0.92);
  padding: 10px 16px 10px 10px;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(0,180,216,0.25);
  transition: all 0.3s ease;
  box-shadow: 0 8px 30px rgba(0,0,0,0.3);

  &:hover {
    border-color: rgba(0,180,216,0.5);
    box-shadow: 0 8px 30px rgba(0,180,216,0.15);
  }
`;

const AudioBtn = styled.button`
  width: 36px; height: 36px;
  background: #00b4d8;
  border: none;
  color: #03045e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;

  &:hover { background: #90e0ef; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AudioLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
`;

// ── HOOKS ──────────────────────────────────────────────────
const useAudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef(null);
  const hasInteracted = useRef(false);

  useEffect(() => {
    let mounted = true;
    const audio = new Audio(AUDIO_URL);
    audioRef.current = audio;
    audio.loop = true;
    audio.volume = INITIAL_VOLUME;
    audio.preload = 'auto';

    audio.addEventListener('canplaythrough', () => {
      if (mounted) { setIsLoading(false); if (hasInteracted.current) audio.play().catch(() => {}); }
    });
    audio.addEventListener('playing', () => { if (mounted) setIsPlaying(true); });
    audio.addEventListener('pause', () => { if (mounted) setIsPlaying(false); });
    audio.addEventListener('error', () => { if (mounted) { setIsLoading(false); setIsPlaying(false); } });

    audio.play().then(() => { hasInteracted.current = true; }).catch(() => setIsPlaying(false));

    const handleVisibility = () => {
      if (document.hidden) audio.pause();
      else if (hasInteracted.current) audio.play().catch(() => {});
    };
    const handleInteraction = async () => {
      if (!hasInteracted.current) {
        hasInteracted.current = true;
        try { await audio.play(); setIsPlaying(true); } catch { setIsPlaying(false); }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      mounted = false;
      audio.pause(); audio.src = ''; audio.remove();
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || isLoading) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => setIsPlaying(false));
  }, [isPlaying, isLoading]);

  return { isPlaying, isLoading, togglePlayPause };
};

const useVisitorCount = () => {
  const [hits, setHits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetch(API_URL)
      .then(r => r.json())
      .then(d => { if (mounted) { setHits(d?.count || d?.data?.count || 0); setIsLoading(false); } })
      .catch(() => { if (mounted) setIsLoading(false); });
    return () => { mounted = false; };
  }, []);

  return { hits, isLoading };
};

// ── COMPONENT ──────────────────────────────────────────────
const Footer = () => {
  const { isPlaying, isLoading, togglePlayPause } = useAudioPlayer();
  const { hits, isLoading: hitsLoading } = useVisitorCount();

  return (
    <>
      <FooterWrap>
        <StatementRow>
          <BigText>
            Inovasi <em>Hidrogen</em><br />
            untuk Indonesia.
          </BigText>
          <VisitorBox>
            <label>Total Visitors</label>
            <strong>{hitsLoading ? '—' : hits.toLocaleString()}</strong>
          </VisitorBox>
        </StatementRow>

        <BottomRow>
          <NavLinks>
            <Link to="/">Beranda</Link>
            <Link to="/fuel-water-hybrid">FUEL WATER HYBRID</Link>
            <Link to="/hidrogenpasta">HIDROGENPASTA</Link>
            <Link to="/hidrofit" className="lowercase">hidrofit⁺</Link>
            <Link to="/kontak">Kontak</Link>
          </NavLinks>
          <Copyright>© {new Date().getFullYear()} Hidrogen.id — Powered by Pass Engineering</Copyright>
        </BottomRow>
      </FooterWrap>

      <AudioControl>
        <AudioBtn onClick={togglePlayPause} disabled={isLoading} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isLoading ? '...' : isPlaying ? <FaPause size={13} /> : <FaPlay size={13} />}
        </AudioBtn>
        <AudioLabel>{isLoading ? 'Loading' : isPlaying ? 'Playing' : 'Paused'}</AudioLabel>
      </AudioControl>
    </>
  );
};

export default memo(Footer);
